import { useEffect, useState } from "react";

import { useAuthContext } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/credits/useCredits";
import { calculateDaysToEndPlan } from "@/utils/plans";
import { useMonitoredCount } from "../useMonitoredCount";

const MONITORING_WAS_PAUSED_PREFIX = "monitoringWasPaused";

/**
 * Per-COMPANY storage key for the "was paused" flag. Scoping by companyId stops
 * the flag from leaking to another company on a shared/kiosk browser — which
 * would otherwise surface a bogus "Reativar monitoramento" prompt (and let the
 * user fire a real POST /legalProceedings/reactivate) for a plan that was never
 * paused. Returns null until the companyId is known, so we never read/write a
 * global key.
 */
export const getMonitoringWasPausedKey = (companyId?: string) =>
  companyId ? `${MONITORING_WAS_PAUSED_PREFIX}:${companyId}` : null;

const readWasPausedFlag = (key: string | null) => {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeWasPausedFlag = (key: string | null, value: boolean) => {
  if (!key) return;
  try {
    if (value) {
      localStorage.setItem(key, "1");
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage unavailable (private mode / SSR) — degrade silently.
  }
};

/**
 * Centralizes the Judit "monitoring retention" state shared across surfaces
 * (cancellation anchor, paused banner, reactivate prompt).
 *
 * - `monitoredCount` (N): number of processes currently monitored.
 * - `isMonitoringPaused`: plan is expired/inactive → new movements stopped (history stays).
 * - `showReactivatePrompt`: plan returned to ACTIVE after having been paused.
 */
export const useMonitoringRetention = () => {
  const { user } = useAuthContext();
  const companyId = user?.companyId;
  const storageKey = getMonitoringWasPausedKey(companyId);

  const { data: credits, isLoading: isLoadingCredits } = useCredits();

  // True billed monitoring count (active + paused trackings), not the imported
  // proceedings totalResults.
  const { data: monitoredCountData } = useMonitoredCount();
  const monitoredCount = (monitoredCountData?.active ?? 0) + (monitoredCountData?.paused ?? 0);

  const companyPlanStatus = credits?.companyPlan?.status;
  const nextBillingDate = credits?.companyPlan?.nextBillingDate;
  const isPlanExpired = calculateDaysToEndPlan(nextBillingDate) < 0;

  const isMonitoringPaused =
    !isLoadingCredits &&
    !!companyPlanStatus &&
    (isPlanExpired || companyPlanStatus === "EXPIRED" || companyPlanStatus === "INACTIVE");

  const isPlanActive = companyPlanStatus === "ACTIVE" && !isPlanExpired;

  const [wasPaused, setWasPaused] = useState<boolean>(false);

  // Re-read the (company-scoped) flag whenever the company changes — so a flag
  // set under company A can never be observed under company B.
  useEffect(() => {
    setWasPaused(readWasPausedFlag(storageKey));
  }, [storageKey]);

  // Persist the "was paused" flag whenever the paused state is rendered, so we
  // can detect the transition back to ACTIVE on a later session.
  useEffect(() => {
    if (storageKey && isMonitoringPaused && !wasPaused) {
      writeWasPausedFlag(storageKey, true);
      setWasPaused(true);
    }
  }, [storageKey, isMonitoringPaused, wasPaused]);

  const showReactivatePrompt = isPlanActive && wasPaused;

  const clearWasPausedFlag = () => {
    writeWasPausedFlag(storageKey, false);
    setWasPaused(false);
  };

  return {
    monitoredCount,
    isMonitoringPaused,
    isPlanActive,
    showReactivatePrompt,
    clearWasPausedFlag,
  };
};
